import { Link } from 'react-router-dom';
import { 
  MapPin, Maximize, Euro, Image as ImageIcon, 
  ShieldCheck, Clock, DoorOpen, Zap, Lock, 
  Truck, ArrowUpFromLine, Briefcase, Package, Bike, ArrowUpToLine,
  Eye, Flame
} from 'lucide-react';
import { formatPrice } from '@/lib/utils';
import { Listing as ListingType } from '@/lib/supabase';

interface ListingCardProps {
  listing: ListingType;
}

export function ListingCard({ listing }: ListingCardProps) {
  // Imagen por defecto si no hay imágenes o falla la carga
  const mainImage = listing.images && listing.images.length > 0 
    ? listing.images[0] 
    : 'https://images.unsplash.com/photo-1590247813693-5541d1c609fd?auto=format&fit=crop&q=80&w=800';

  // Lógica de Etiquetas Inteligentes (Smart Tags)
  const getBadges = () => {
    const badges = [];
    const f = listing.features || {};

    // 1. SEGURIDAD Y CONFIANZA (Verde - Prioridad Alta)
    if (f.feat_security && f.feat_24h) {
      badges.push({ icon: ShieldCheck, label: 'Alta Seguridad', color: 'text-emerald-700 bg-emerald-50 border-emerald-200' });
    } else if (f.feat_24h) {
      badges.push({ icon: Clock, label: 'Acceso 24h', color: 'text-emerald-700 bg-emerald-50 border-emerald-200' });
    } else if (f.feat_security) {
      badges.push({ icon: ShieldCheck, label: 'Vigilancia', color: 'text-emerald-700 bg-emerald-50 border-emerald-200' });
    }

    // 2. COMODIDAD Y ACCESO (Ámbar - Prioridad Media)
    if (f.access_street) {
      badges.push({ icon: DoorOpen, label: 'Pie de calle', color: 'text-amber-700 bg-amber-50 border-amber-200' });
    } else if (f.access_garage) {
      badges.push({ icon: Truck, label: 'Carga Fácil', color: 'text-amber-700 bg-amber-50 border-amber-200' });
    } else if (f.access_elevator || f.access_freight_elevator) {
      badges.push({ icon: ArrowUpFromLine, label: 'Con Ascensor', color: 'text-amber-700 bg-amber-50 border-amber-200' });
    }

    // 3. USOS ESPECIALES (Azul - Prioridad Media)
    if (f.use_stock || f.use_office || f.use_documents) {
      badges.push({ icon: Briefcase, label: 'Ideal Negocios', color: 'text-blue-700 bg-blue-50 border-blue-200' });
    } else if (f.use_moving && (f.access_street || f.access_garage || f.access_freight_elevator)) {
      badges.push({ icon: Package, label: 'Apto Mudanzas', color: 'text-blue-700 bg-blue-50 border-blue-200' });
    } else if (f.use_bicycle) {
      badges.push({ icon: Bike, label: 'Guarda-Bicis', color: 'text-blue-700 bg-blue-50 border-blue-200' });
    }

    // 4. EXTRAS (Relleno si hay hueco - Prioridad Baja)
    if (badges.length < 3) {
      if (listing.height_m && listing.height_m > 3) {
        badges.push({ icon: ArrowUpToLine, label: 'Techo Alto', color: 'text-slate-700 bg-slate-100 border-slate-200' });
      } else if (f.feat_light || f.feat_power) {
        badges.push({ icon: Zap, label: 'Con Luz', color: 'text-yellow-700 bg-yellow-50 border-yellow-200' });
      } else if (f.feat_lock) {
        badges.push({ icon: Lock, label: 'Cerradura', color: 'text-slate-700 bg-slate-100 border-slate-200' });
      }
    }

    return badges.slice(0, 3); // Máximo 3 badges para mantener el diseño limpio
  };

  const featuredBadges = getBadges();
  const viewCount = listing.view_count || 0;
  const isHighDemand = viewCount > 50;

  return (
    <Link 
      to={`/listings/${listing.id}`}
      className="group bg-white border border-slate-200 rounded-xl overflow-hidden hover:shadow-lg transition-all duration-300 flex flex-col h-full relative"
    >
      <div className="relative aspect-[4/3] overflow-hidden bg-slate-100">
        <img 
          src={mainImage} 
          alt={listing.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          loading="lazy"
        />
        <div className="absolute top-3 left-3 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full text-xs font-bold text-slate-700 uppercase tracking-wide border border-white/50 shadow-sm">
          {listing.operation_type === 'rent' ? 'Alquiler' : 'Venta'}
        </div>
        
        {/* Badge de Alta Demanda en la imagen si es muy popular */}
        {isHighDemand && (
             <div className="absolute top-3 right-3 bg-red-600 text-white px-2 py-1 rounded-full text-[10px] font-bold uppercase tracking-wide shadow-sm flex items-center gap-1 animate-pulse">
                <Flame className="w-3 h-3" /> Muy solicitado
             </div>
        )}

        {listing.images && listing.images.length > 0 && (
          <div className="absolute bottom-3 right-3 bg-black/50 backdrop-blur-sm px-2 py-1 rounded-md text-xs font-medium text-white flex items-center gap-1">
            <ImageIcon className="w-3 h-3" />
            {listing.images.length}
          </div>
        )}
      </div>
      
      <div className="p-4 flex flex-col flex-1">
        <div className="flex justify-between items-start mb-2">
          <h3 className="text-lg font-bold text-slate-900 line-clamp-1 group-hover:text-blue-600 transition-colors">
            {listing.title}
          </h3>
        </div>
        
        {/* Ubicación y Contador de Visitas */}
        <div className="flex justify-between items-center mb-4">
            <div className="flex items-center text-slate-500 text-sm overflow-hidden">
                <MapPin className="w-4 h-4 mr-1 shrink-0" />
                <span className="truncate">{listing.city}{listing.province ? `, ${listing.province}` : ''}</span>
            </div>

            {/* Contador de Visitas (Elemento de Urgencia) */}
            {viewCount > 0 && (
                <div className={`flex items-center text-xs font-medium px-2 py-0.5 rounded-full border shrink-0 ml-2 ${
                    isHighDemand 
                        ? 'text-red-600 bg-red-50 border-red-100' 
                        : 'text-slate-500 bg-slate-50 border-slate-100'
                }`} title={`${viewCount} personas han visto este anuncio`}>
                    {isHighDemand ? <Flame className="w-3 h-3 mr-1" /> : <Eye className="w-3 h-3 mr-1" />}
                    {viewCount}
                </div>
            )}
        </div>

        <div className="grid grid-cols-2 gap-2 mb-3">
          <div className="bg-slate-50 rounded-lg p-2 flex items-center justify-center gap-2 text-sm text-slate-600 border border-slate-100">
            <Maximize className="w-4 h-4 text-blue-500" />
            <span className="font-medium">{listing.area_m2} m²</span>
          </div>
          <div className="bg-slate-50 rounded-lg p-2 flex items-center justify-center gap-2 text-sm text-slate-600 border border-slate-100">
            <Euro className="w-4 h-4 text-green-500" />
            <span className="font-medium">
              {listing.operation_type === 'rent' ? '/mes' : '/total'}
            </span>
          </div>
        </div>

        {/* Badges de características destacadas (Smart Tags) */}
        <div className="flex flex-wrap gap-2 mb-4 h-[28px]"> {/* Altura fija para evitar saltos si no hay badges */}
          {featuredBadges.length > 0 ? (
            featuredBadges.map((badge, idx) => (
              <span key={idx} className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] sm:text-xs font-semibold border ${badge.color}`}>
                <badge.icon className="w-3 h-3" />
                {badge.label}
              </span>
            ))
          ) : (
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium text-slate-400 bg-slate-50 border border-slate-100">
              <Maximize className="w-3 h-3" /> Espacio Básico
            </span>
          )}
        </div>

        <div className="mt-auto pt-3 border-t border-slate-100 flex items-center justify-between">
          <span className="text-2xl font-bold text-slate-900">
            {formatPrice(listing.price)}
            {listing.operation_type === 'rent' && <span className="text-sm text-slate-500 font-normal">/mes</span>}
          </span>
          <span className="text-sm font-medium text-blue-600 group-hover:underline">
            Ver detalles
          </span>
        </div>
      </div>
    </Link>
  );
}
